import React, { useState, useEffect } from 'react';
import { Upload, Trash2, RotateCw, Image as ImageIcon, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const GalleryManager = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // Use current domain for API calls (works on all domains)
  // Fallback to window.location.origin if REACT_APP_BACKEND_URL is empty
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    type: '',
    year: new Date().getFullYear().toString(),
    description: '',
    image: null
  });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/gallery?per_page=100`);
      const data = await response.json();
      setImages(data.images || []);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Erreur lors du chargement des images');
    } finally {
      setLoading(false);
    }
  };

  // Images are served from frontend/public, URLs are relative - use directly

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('📸 Fichier sélectionné:', {
        nom: file.name,
        type: file.type,
        taille: (file.size / 1024 / 1024).toFixed(2) + ' MB'
      });
      
      // Vérifier la taille (20 MB max pour mobile)
      if (file.size > 20 * 1024 * 1024) {
        const msg = `L'image est trop grosse (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 20 MB.`;
        toast.error(msg);
        console.error('❌', msg);
        return;
      }
      
      // Accepter tous les formats d'images, y compris HEIC
      const isImage = file.type.startsWith('image/') || 
                     file.name.toLowerCase().endsWith('.heic') || 
                     file.name.toLowerCase().endsWith('.heif');
      
      if (!isImage) {
        const msg = 'Veuillez sélectionner une image';
        toast.error(msg);
        console.error('❌', msg, 'Type:', file.type);
        return;
      }
      
      setFormData({ ...formData, image: file });
      
      // Créer la preview
      // Note: HEIC/HEIF ne peuvent pas être prévisualisés dans le navigateur
      const isHEIC = file.name.toLowerCase().endsWith('.heic') || 
                     file.name.toLowerCase().endsWith('.heif');
      
      if (isHEIC) {
        console.log('ℹ️ Format HEIC détecté - Aperçu non disponible (normal sur Mac)');
        toast.info('📸 Image HEIC sélectionnée (aperçu non disponible, mais l\'upload fonctionnera)');
        setPreviewUrl('heic'); // Marqueur spécial pour afficher un placeholder
      } else {
        try {
          const objectUrl = URL.createObjectURL(file);
          setPreviewUrl(objectUrl);
          console.log('✅ Aperçu créé');
        } catch (e) {
          console.warn('⚠️ Impossible de créer l\'aperçu:', e);
          setPreviewUrl('error');
        }
      }
      
      console.log('✅ Image prête à être uploadée');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🔵 handleSubmit appelé');
    console.log('📋 FormData state:', {
      hasImage: !!formData.image,
      title: formData.title,
      location: formData.location,
      type: formData.type,
      year: formData.year,
      description: formData.description
    });
    
    if (!formData.image) {
      toast.error('Veuillez sélectionner une image');
      console.error('❌ Pas d\'image sélectionnée');
      return;
    }
    
    if (!formData.title || !formData.location || !formData.type || !formData.year || !formData.description) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      console.error('❌ Champs manquants');
      return;
    }

    try {
      setUploading(true);
      console.log('⏳ setUploading(true)');
      
      const token = localStorage.getItem('admin_token');

      if (!token) {
        const msg = 'Session expirée, veuillez vous reconnecter';
        toast.error(msg);
        console.error('❌', msg);
        navigate('/admin/login');
        return;
      }

      console.log('🚀 DÉBUT UPLOAD');
      console.log('📦 Données:', {
        titre: formData.title,
        lieu: formData.location,
        imageNom: formData.image.name,
        imageType: formData.image.type,
        imageTaille: (formData.image.size / 1024 / 1024).toFixed(2) + ' MB',
        backendURL: BACKEND_URL,
        tokenPresent: !!token
      });

      const formDataToSend = new FormData();
      // IMPORTANT: L'image doit être en premier pour correspondre à l'ordre du backend
      formDataToSend.append('image', formData.image);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('location', formData.location);
      formDataToSend.append('type', formData.type);
      formDataToSend.append('year', formData.year);
      formDataToSend.append('description', formData.description);

      console.log('📤 Envoi de la requête vers:', `${BACKEND_URL}/api/gallery/upload`);

      const response = await fetch(`${BACKEND_URL}/api/gallery/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      console.log('📥 Réponse reçue:', response.status, response.statusText);

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        console.log('🔍 Content-Type:', contentType);
        
        let errorDetail = `Erreur ${response.status}: ${response.statusText}`;
        
        try {
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            console.log('📋 Erreur JSON:', errorData);
            errorDetail = errorData.detail || errorData.message || JSON.stringify(errorData);
          } else {
            const textError = await response.text();
            console.log('📋 Erreur texte:', textError);
            errorDetail = textError || errorDetail;
          }
        } catch (e) {
          console.error('⚠️ Erreur lors de la lecture du corps:', e);
        }
        
        console.error('❌ ERREUR SERVEUR:', errorDetail);
        throw new Error(errorDetail);
      }

      const result = await response.json();
      console.log('✅ UPLOAD RÉUSSI !', result);

      // Only update UI on verified success
      toast.success('✅ Image uploadée avec succès !', {
        duration: 3000,
        position: 'top-center'
      });
      
      // Reset form
      setFormData({
        title: '',
        location: '',
        type: '',
        year: new Date().getFullYear().toString(),
        description: '',
        image: null
      });
      setPreviewUrl(null);
      
      // Refresh images list
      console.log('🔄 Rechargement de la liste...');
      await fetchImages();
      
    } catch (error) {
      console.error('❌ ERREUR UPLOAD:', error);
      
      // Distinguish between network errors and server errors
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        toast.error('❌ Erreur de connexion. Vérifiez votre réseau ou réessayez.', {
          duration: 5000,
          position: 'top-center'
        });
      } else {
        toast.error(error.message || 'Erreur lors de l\'upload', {
          duration: 5000,
          position: 'top-center'
        });
      }
      
      // DO NOT update state on error - prevent fake success UI
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');

      const response = await fetch(`${BACKEND_URL}/api/gallery/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }

      toast.success('Image supprimée avec succès');
      fetchImages();
      
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleRotate = async (imageId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/gallery/${imageId}/rotate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors de la rotation');
      }

      toast.success('🔄 Image tournée de 90°');
      
      // Force reload images with cache busting
      setTimeout(() => {
        fetchImages();
        // Force image refresh by adding timestamp to src
        window.location.reload();
      }, 300);
    } catch (error) {
      console.error('Error rotating image:', error);
      toast.error(error.message || 'Erreur lors de la rotation');
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-slate-900">Gestion de la Galerie</h1>
            <p className="text-gray-600">Uploadez et gérez les photos de vos interventions</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Total d'images</p>
            <p className="text-2xl font-bold text-orange-600">{images.length}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Upload Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                <Upload className="w-5 h-5 mr-2 text-orange-600" />
                Ajouter une Image
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Photo <span className="text-orange-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-orange-500 transition-colors cursor-pointer">
                    <input
                      type="file"
                      name="image"
                      accept="image/*,image/heic,image/heif"
                      onChange={handleImageSelect}
                      className="hidden"
                      id="image-upload"
                    />
                    <label htmlFor="image-upload" className="cursor-pointer">
                      {previewUrl && previewUrl !== 'heic' && previewUrl !== 'error' ? (
                        <img src={previewUrl} alt="Preview" className="w-full h-40 object-cover rounded-lg mb-2" />
                      ) : previewUrl === 'heic' ? (
                        <div className="w-full h-40 bg-gradient-to-br from-orange-100 to-orange-50 rounded-lg mb-2 flex flex-col items-center justify-center">
                          <ImageIcon className="w-12 h-12 text-orange-500 mb-2" />
                          <p className="text-sm font-medium text-orange-600">📸 Image HEIC sélectionnée</p>
                          <p className="text-xs text-gray-500">{formData.image?.name}</p>
                          <p className="text-xs text-green-600 mt-1">✅ Prête pour l'upload</p>
                        </div>
                      ) : previewUrl === 'error' ? (
                        <div className="w-full h-40 bg-gray-100 rounded-lg mb-2 flex flex-col items-center justify-center">
                          <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">Image sélectionnée</p>
                          <p className="text-xs text-gray-500">{formData.image?.name}</p>
                        </div>
                      ) : (
                        <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      )}
                      <p className="text-sm text-gray-600">Cliquez pour sélectionner</p>
                      <p className="text-xs text-gray-400">Max 20 MB • Tous formats acceptés</p>
                      <p className="text-xs text-orange-500 mt-1">📷 Caméra ou 🖼️ Galerie</p>
                    </label>
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Titre <span className="text-orange-500">*</span>
                  </label>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Ex: Installation complète de ponts hydrauliques"
                    required
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Lieu / Client <span className="text-orange-500">*</span>
                  </label>
                  <Input
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Ex: Plateforme logistique - Paris"
                    required
                  />
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Type d'équipement <span className="text-orange-500">*</span>
                  </label>
                  <Input
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    placeholder="Ex: Ponts hydrauliques"
                    required
                  />
                </div>

                {/* Year */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Année <span className="text-orange-500">*</span>
                  </label>
                  <Input
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    placeholder="2024"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Description <span className="text-orange-500">*</span>
                  </label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Décrivez brièvement l'intervention..."
                    rows={3}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={uploading}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Upload en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Uploader l'image
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Images Grid */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6">
                Images de la Galerie ({images.length})
              </h2>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
                  <p className="text-gray-600 mt-4">Chargement...</p>
                </div>
              ) : images.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ImageIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>Aucune image pour le moment</p>
                  <p className="text-sm">Uploadez votre première image !</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {images.map((img) => (
                    <div key={img.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                      <img 
                        src={img.image_url} 
                        alt={img.title}
                        className="w-full h-48 object-cover"
                      />
                      <div className="p-4">
                        <h3 className="font-bold text-slate-900 mb-1">{img.title}</h3>
                        <p className="text-sm text-gray-600 mb-1">{img.location}</p>
                        <p className="text-xs text-gray-500 mb-2">{img.type} • {img.year}</p>
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">{img.description}</p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRotate(img.id)}
                            className="flex-1"
                            title="Tourner de 90°"
                          >
                            <RotateCw className="w-4 h-4 mr-2" />
                            Rotation
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(img.id)}
                            className="flex-1"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GalleryManager;
